/** E2c frontend — THE one governed entity-link renderer.
 *
 * Contract (build plan §1j, workspace spec §7.6), frozen by the design owner 2026-07-31:
 *
 *     field value is visible to viewer
 *     AND target entity is visible to viewer at L2 metadata or better
 *     AND target id is live/canonical
 *
 * The backend enforces it — `crm/entity_links.resolve_entity_links` returns only the refs a viewer
 * may see, overwrites the name from the governed directory, and `scrub_ref_cell` DROPS any ref whose
 * target is absent. This module is the read side finally matching it.
 *
 * ⚠ WHY THIS FILE EXISTS: three renderers each carried `r.name || r.email || '#' + id`.
 * That fallback breaks the contract in two ways at once. **L1 / class-ceiling withholds BOTH the id
 * and the name** — so printing `#4213` for a ref whose name was withheld hands over the exact
 * identifier the server refused, and it is a stable one a reader can correlate across deals. And
 * `r.email` prints a personal email address that E2b-2 stopped writing into new payloads precisely
 * because it should never have been a display fallback; legacy `field_values_json` rows still carry
 * it, so the read side has to be the thing that stops rendering it.
 *
 * So: **a ref renders by NAME, or as an authorized `(unnamed)` chip, or not at all.** There is no id
 * or email fallback. A withheld ref is omitted, and a cell left with nothing renders as an ordinary
 * empty cell — exactly the server's posture, where absence deliberately collapses unauthorized /
 * missing / deleted / merged-away so the SHAPE of the response cannot leak a withheld target's
 * existence.
 *
 * ⚠ The authorization signal is `open_profile`, NOT name truthiness — see `isAuthorized`. Getting
 * that backwards hides authorized bare-email contacts, which is a different bug in the opposite
 * direction and is what the first cut of this module did.
 *
 * This also makes the E2e-2 projection safe on any surface: a projected cell is
 * `{local_id: N, name: null}` with no `open_profile`, so it renders as nothing until it has passed
 * the governed resolve — never as a bare id.
 */
import type { CrmRefValue } from '../../types';
import { formatRoles, profileHref } from './crmEntityRoles';
// The shared CRM stylesheet both the grid and the panel already use — `crm.module.css`, not a
// per-component one. Same classes, so consolidating the renderer changes no pixel.
import styles from './crm.module.css';

/**
 * The id-bearing keys a stored ref may carry. **Mirrors the server's `entity_links._LOCAL_ID_KEYS`**,
 * and the two must stay in step: the backend gate collects refs with `local_id_of`, which reads every
 * one of these, so a key the frontend does not recognize is a ref the server scrubbed and authorized
 * and the frontend then silently declines to draw.
 */
const REF_ID_KEYS = [
  'affinity_id', 'local_id', 'local_person_id', 'local_org_id', 'local_organization_id',
] as const;

/**
 * True for anything shaped like an entity ref, in any shape stored over time.
 *
 * ⚠ **This must recognize exactly what `refLocalId` can read.** It used to test only `affinity_id` and
 * `local_id` while `refLocalId` also read `local_person_id` / `local_org_id` — so a ref carrying only
 * one of those was dropped by `visibleRefs` before anything could resolve it. Harmless in practice
 * only because the import happens to write `affinity_id` alongside; that is a coincidence, not a
 * contract, and it is the same "two functions disagree about what counts as a ref" failure that made
 * #809's gate see zero refs in every imported person cell.
 */
export function isRef(v: unknown): v is CrmRefValue {
  return typeof v === 'object' && v !== null
    && REF_ID_KEYS.some((k) => k in (v as object));
}

/** Placeholder for an AUTHORIZED target that simply has no name on record. */
export const UNNAMED_LABEL = '(unnamed)';

/**
 * ⚠ `open_profile` IS THE AUTHORIZATION SIGNAL — not name truthiness.
 *
 * The server is explicit about this (`entity_links.py`: "`None` is legitimate (a bare-email
 * directory contact) and is NOT 'not found' — presence in this map is the authorization signal,
 * never name truthiness"). `scrub_ref_cell` DROPS every withheld ref and rebuilds the survivors with
 * `open_profile` attached, so on a scrubbed surface a `name: null` ref is one the viewer IS allowed
 * to see and which merely has no name on record.
 *
 * The first cut of this module inverted that: it hid any nameless ref, so an authorized bare-email
 * contact vanished from the deal and the editor labelled them "Restricted" — telling an operator
 * they lacked permission they actually had. Caught by adversarial review, 2026-08-02.
 */
export function isAuthorized(r: CrmRefValue): boolean {
  return (r as { open_profile?: unknown }).open_profile != null;
}

/**
 * Whether this ref's name should render as a LINK — `internal-profile-visibility-spec.md` §3.3.
 *
 * ⚠ **This is NOT a second authorization check, and it deliberately does not consult `isAuthorized`.**
 * Two separate reasons, and getting either wrong is a bug in a different direction:
 *
 *  - Linkability is a narrower question than visibility. An `@ember-infra.com` person's profile page is
 *    unreachable for non-admins (existence-safe 404), but the person is still on the deal team and must
 *    still RENDER — spec §3.4 is explicit that deal-team columns and role groups are names, not
 *    profiles, and gating them breaks the IOT board. So the server marks such a ref `profile_hidden`
 *    ALONGSIDE `open_profile` rather than withholding `open_profile`, which would make `isAuthorized`
 *    false and delete the colleague from the panel altogether.
 *  - ANDing `isAuthorized` in here would ALSO un-link every ref that legitimately has no
 *    `open_profile` — a stored `{local_id, name}` cell, or the IOT import's `local_organization_id`
 *    shape — turning working links into plain text across the grid. Authorization is enforced at the
 *    LABEL (`refLabel` → `visibleRefs`); by the time a ref is being rendered, the question left is only
 *    "does this thing have a page to open".
 *
 * The decision is the server's, not ours: the ref payload deliberately carries no email (E2b-2 stopped
 * writing one), so the front end could not apply the domain rule even if it should — and it should not.
 * One predicate, three consumers.
 *
 * Absent flag → linkable, so every payload written before this existed behaves exactly as it did.
 */
export function canOpenProfile(r: CrmRefValue): boolean {
  return (r as { profile_hidden?: unknown }).profile_hidden !== true;
}

/**
 * How a ref should be labelled, or null when it must not be rendered at all.
 *
 * Never the email, never the id — those are what the two-gate contract withholds. Returns null
 * rather than a placeholder so the caller, which knows whether the cell then has anything left,
 * owns the decision to show an empty cell.
 */
export function refLabel(r: CrmRefValue): string | null {
  const name = (r.name ?? '').trim();
  if (name !== '') return name;
  // Authorized but nameless → a visible, clickable placeholder. Unauthorized/unresolved → nothing.
  // An E2e-2 projected ref (`{local_id, name: null}` with no `open_profile`) has not been through
  // the governed resolve, so it correctly falls in the second case.
  return isAuthorized(r) ? UNNAMED_LABEL : null;
}

/** Every ref in a cell value that this viewer may actually see rendered. */
export function visibleRefs(value: unknown): CrmRefValue[] {
  const items = Array.isArray(value) ? value : [value];
  return items.filter(isRef).filter((r) => refLabel(r) !== null);
}

/**
 * Which local record a ref clicks through to, or null when it has none.
 *
 * An `affinity_id`-only ref has no local record to open, so it renders as a plain chip rather than a
 * dead link. Note `local_id` is read LAST for a person: `local_person_id` is the explicit field and
 * `local_id` is the generic one, and preferring the explicit one keeps a mixed-shape payload from
 * opening the wrong profile.
 */
export function refLocalId(r: CrmRefValue, kind: 'person' | 'organization'): number | null {
  const id = kind === 'person'
    ? (r.local_person_id ?? r.local_id ?? null)
    : (r.local_org_id ?? r.local_organization_id ?? r.local_id ?? null);
  return typeof id === 'number' ? id : null;
}

/**
 * The chip's label with its role suffix — `Weil Gotshal (Legal)` (memo §15 items 1 + 2).
 *
 * Only the fact-backed catch-all columns carry roles; a role-FILTERED column already states its role
 * in its header, so the server does not emit one there and this is a no-op for it.
 */
export function refLabelWithRole(r: CrmRefValue): string | null {
  const base = refLabel(r);
  if (base === null) return null;
  // `implied_roles` (party-facts §5 rule 2) folds the slugs another chip on this deal already
  // entails, so the suffix says the one thing that is news rather than repeating its neighbour.
  const roles = formatRoles(r.roles, r.implied_roles);
  return roles ? `${base} (${roles})` : base;
}

export interface EntityRefsProps {
  value: unknown;
  kind: 'person' | 'organization';
  /** Click-through. Omitted → chips render as plain text (the panel's read-only presentation). */
  onOpen?: (id: number) => void;
  /** Rendered when nothing is visible. Defaults to the standard empty-cell dash. */
  emptyNode?: React.ReactNode;
  /**
   * Render chips as real links that open the profile in a NEW TAB (memo §15 item 2), instead of
   * navigating in place.
   *
   * Opt-in per surface rather than global: on a deal panel you are scanning a list of advisors and
   * navigating away in place loses the deal you were reading, which is why the owner asked for new
   * tabs there. Elsewhere — a profile's own related-entity lists, the grid's ordinary ref cells —
   * in-place navigation is the right behaviour and stays untouched.
   */
  newTab?: boolean;
}

/** The one renderer. Every surface that shows entity refs goes through this. */
export function EntityRefs({ value, kind, onOpen, emptyNode, newTab }: EntityRefsProps) {
  const refs = visibleRefs(value);
  if (refs.length === 0) {
    return <>{emptyNode ?? <span className={styles.gridEmpty}>—</span>}</>;
  }
  return (
    <span className={styles.gridChips}>
      {refs.map((r, i) => {
        const label = refLabelWithRole(r) as string;
        // §3.3: a ref whose profile is hidden keeps its id (it is authorized, and the id is used for
        // keys/edits) but must NOT click through — otherwise every colleague on the panel is a link
        // that 404s. Falls through to the plain-chip branch, exactly like an affinity_id-only ref.
        const id = canOpenProfile(r) ? refLocalId(r, kind) : null;
        if (id != null && newTab) {
          // A real anchor, so middle-click / ⌘-click / "open in new tab" all behave. `rel` is not
          // optional on a `_blank` target — without `noopener` the opened page gets a handle on this
          // window via `window.opener`.
          return (
            <a
              key={i}
              className={styles.gridRefLink}
              href={profileHref(kind, id)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {label}
            </a>
          );
        }
        return id != null && onOpen ? (
          <button key={i} type="button" className={styles.gridRefLink} onClick={() => onOpen(id)}>
            {label}
          </button>
        ) : (
          <span key={i} className={styles.gridRef}>{label}</span>
        );
      })}
    </span>
  );
}
